import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const Tabs = TabsPrimitive.Root;

/**
 * Internal context that propagates the TabsList variant to TabsTrigger children.
 * This avoids requiring callers to pass variant to every individual trigger.
 */
const TabsVariantContext = React.createContext<
  'default' | 'underline' | 'pill' | 'pill-solid'
>('default');

/**
 * TabsList supports four visual variants:
 * - default:   bottom-border underline row (standard Xero-style tab bar)
 * - underline:  same layout without container background/border
 * - pill:      pill-style tab switcher (maps to .nv-tab-pill glass token)
 * - pill-solid: rounded pills whose active tab is a filled navy pill with
 *               white text (maps to .nv-tab-pill-solid) — statutory hub strip
 */
const tabs_list_variants = cva('inline-flex h-auto items-center', {
  variants: {
    variant: {
      default: 'gap-0 glass-nav',
      underline: 'gap-0 glass-nav',
      pill: 'gap-1 bg-transparent',
      'pill-solid': 'gap-1 bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type TabsListVariantProps = VariantProps<typeof tabs_list_variants>;

export type TabsProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Root
>;

export interface TabsListProps
  extends
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    TabsListVariantProps {}

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, ...props }, ref) => (
  <TabsVariantContext.Provider value={variant ?? 'default'}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabs_list_variants({ variant }), className)}
      {...props}
    />
  </TabsVariantContext.Provider>
));
TabsList.displayName = 'TabsList';

/**
 * TabsTrigger wired to .nv-tab / .nv-tab-pill glass tokens.
 *
 * The variant is inherited from the enclosing TabsList via context.
 * Active state is bridged from Radix's data-[state=active] attribute
 * to the glass token's .is-active visual spec via Tailwind data-attribute
 * overrides (avoids adding an explicit .is-active class via JS).
 */
export type TabsTriggerProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
>;

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        // Accessibility and interaction affordances (all variants)
        'whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        variant === 'pill-solid'
          ? // .nv-tab-pill-solid owns base + active (filled navy pill, white
            // text) via [data-state=active] in glass.css — no Tailwind override.
            ['nv-tab-pill-solid']
          : variant === 'pill'
            ? [
                // .nv-tab-pill: padding 0.4rem 0.75rem, font-size 13px, border-radius 8px
                'nv-tab-pill',
                // Active state mirrors .nv-tab-pill.is-active
                'data-[state=active]:bg-[var(--color-accent-soft)] data-[state=active]:text-[var(--accent)] data-[state=active]:font-semibold',
              ]
            : [
                // .nv-tab: padding 0.55rem 0.9rem, font-size 13.5px, border-bottom placeholder
                'nv-tab',
                // Active state mirrors .nv-tab.is-active
                'data-[state=active]:border-b-[var(--accent)] data-[state=active]:text-[var(--accent)] data-[state=active]:font-semibold',
              ],
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = 'TabsTrigger';

export type TabsContentProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Content
>;

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 animate-in fade-in duration-200 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsList, TabsTrigger, TabsContent };
